import { useEffect, useMemo, useRef, useState } from 'react';

import { NOTE_MAX_LENGTH } from '@time-tracking/shared';

import { useFocusTrap } from '../hooks/useFocusTrap.js';

/** The value a draft is persisted as — `onSave` stores trimmed text, and blank as null. */
function toSavedNote(text: string): string | null {
  return text.trim() || null;
}

interface NotePopoverProps {
  lineId: string;
  taskName: string;
  initialNote: string | null;
  readOnly: boolean;
  anchorRect: DOMRect;
  onClose: () => void;
  onSave: (note: string | null) => void;
  isSaving: boolean;
}

export function NotePopover({
  taskName,
  initialNote,
  readOnly,
  anchorRect,
  onClose,
  onSave,
  isSaving,
}: NotePopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState(initialNote ?? '');
  // True while the "You have unsaved changes" prompt replaces the footer (#116).
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  useFocusTrap(containerRef, true);

  // Dirty means saving would change what is stored — compared through the same
  // normalisation `onSave` applies, so trailing whitespace alone never prompts.
  const isDirty = !readOnly && toSavedNote(draft) !== toSavedNote(initialNote ?? '');

  // The window listeners below are registered once per `onClose`; refs let them read
  // the current draft state without re-subscribing on every keystroke.
  const isDirtyRef = useRef(isDirty);
  const confirmingRef = useRef(confirmingDiscard);
  useEffect(() => {
    isDirtyRef.current = isDirty;
    confirmingRef.current = confirmingDiscard;
  });

  // The prompt leaves focus in the textarea so the user can simply keep typing; Save
  // and Discard are a Tab away. Only if focus was in the footer the prompt replaces
  // (Escape from its Save/Cancel) does it need putting back.
  useEffect(() => {
    if (confirmingDiscard && !containerRef.current?.contains(document.activeElement)) {
      textareaRef.current?.focus();
    }
  }, [confirmingDiscard]);

  // Position: flip above when not enough space below; anchor to right edge of trigger
  const style = useMemo(() => {
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const right = Math.max(12, window.innerWidth - anchorRect.right);

    if (spaceBelow < 280) {
      return {
        bottom: window.innerHeight - anchorRect.top + 6,
        right,
      };
    }
    return {
      top: anchorRect.bottom + 6,
      right,
    };
  }, [anchorRect]);

  // Every implicit dismissal funnels through here. A draft with unsaved changes is
  // never dropped by one: only the prompt's Save / Discard (or the explicit Cancel
  // button) closes it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Claimed either way, so any other Escape handler checking defaultPrevented
      // leaves the page alone while this dialog is up.
      e.preventDefault();
      if (confirmingRef.current) {
        // Escape dismisses the innermost layer — the prompt — not the draft.
        setConfirmingDiscard(false);
        textareaRef.current?.focus();
      } else if (isDirtyRef.current) {
        setConfirmingDiscard(true);
      } else {
        onClose();
      }
    };
    const isOutside = (e: Event) =>
      containerRef.current !== null && !containerRef.current.contains(e.target as Node);
    const onOutsideMouseDown = (e: MouseEvent) => {
      if (!isOutside(e)) return;
      if (!isDirtyRef.current) {
        onClose();
        return;
      }
      // Keep focus inside the popover and keep the press from reaching the page.
      e.preventDefault();
      e.stopPropagation();
      setConfirmingDiscard(true);
    };
    // While dirty, the click that follows an outside press is swallowed too. Without
    // this, pressing another row's note button, a week arrow or a nav link would act
    // anyway — re-keying or unmounting this popover and losing the draft regardless.
    const onOutsideClick = (e: MouseEvent) => {
      if (!isOutside(e) || !isDirtyRef.current) return;
      e.preventDefault();
      e.stopPropagation();
    };
    // Reload, tab close or an off-app link would unmount the popover with no chance
    // to prompt; let the browser ask instead.
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      // Legacy browsers only show the dialog when returnValue is set.
      e.returnValue = '';
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('beforeunload', onBeforeUnload);
    // Capture phase, so a dirty draft's guard runs before React's root listeners.
    // Delay 1 tick to avoid the same click that opened the popover from closing it.
    const timer = setTimeout(() => {
      window.addEventListener('mousedown', onOutsideMouseDown, true);
      window.addEventListener('click', onOutsideClick, true);
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('beforeunload', onBeforeUnload);
      clearTimeout(timer);
      window.removeEventListener('mousedown', onOutsideMouseDown, true);
      window.removeEventListener('click', onOutsideClick, true);
    };
  }, [onClose]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!isSaving) onSave(toSavedNote(draft));
    }
  };

  const atMax = draft.length >= NOTE_MAX_LENGTH;

  if (readOnly) {
    return (
      <div
        ref={containerRef}
        className="note-pop"
        style={{ position: 'fixed', zIndex: 120, ...style }}
        role="dialog"
        aria-modal="true"
        aria-label={`Note for approver — ${taskName}`}
        data-testid="line-note-popover"
      >
        <div className="note-pop__head">
          <div className="note-pop__title">Note for approver</div>
          <div className="note-pop__sub">{taskName}</div>
        </div>
        {/* Focusable so a long note that scrolls (max-height in globals.css) can be
            scrolled from the keyboard — WCAG 2.1.1, axe scrollable-region-focusable. */}
        <div
          className="note-pop__readonly-box"
          tabIndex={0}
          role="group"
          aria-label="Note text (read-only)"
          data-testid="note-readonly"
        >
          {initialNote}
        </div>
        <div className="note-pop__footer">
          <span className="note-pop__footer-text">Visible to your approver · read-only</span>
          <div className="note-pop__actions">
            <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const heading = initialNote ? 'Edit note' : 'Note for approver';

  return (
    <div
      ref={containerRef}
      className="note-pop"
      style={{ position: 'fixed', zIndex: 120, ...style }}
      role="dialog"
      aria-modal="true"
      aria-label={`${heading} — ${taskName}`}
      data-testid="line-note-popover"
    >
      <div className="note-pop__head">
        <div className="note-pop__title">{heading}</div>
        <div className="note-pop__sub">{taskName}</div>
      </div>
      <textarea
        ref={textareaRef}
        className="note-pop__textarea"
        maxLength={NOTE_MAX_LENGTH}
        autoFocus // intentional: focus textarea when popover opens for keyboard efficiency
        value={draft}
        placeholder="Optional context for this line — e.g. why a spike, or what got done."
        aria-label="Note text"
        aria-describedby="note-pop-count"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <div className="note-pop__meta-row">
        {/* Described-by the textarea (read on focus) rather than aria-live: at a
            1000-char cap a live counter would announce on every keystroke. Same
            pattern as SubmitDialog. */}
        <span
          id="note-pop-count"
          className={`note-count${atMax ? ' note-count--max' : ''}`}
          data-testid="note-counter"
        >
          {draft.length}/{NOTE_MAX_LENGTH}
        </span>
      </div>
      {/* Always mounted so the message is a change to an existing live region —
          screen readers frequently stay silent for a region mounted pre-populated. */}
      {/* No group name on the wrapper: the alert already reads the message, and a
          group labelled by the same text would have it read twice. */}
      <div
        className={confirmingDiscard ? 'note-pop__unsaved' : undefined}
        data-testid="note-unsaved-prompt"
      >
        <p className="note-pop__unsaved-msg" role="alert">
          {confirmingDiscard ? 'You have unsaved changes' : ''}
        </p>
        {confirmingDiscard && (
          <div className="note-pop__actions">
            <button
              type="button"
              className="btn primary btn--sm"
              onClick={() => onSave(toSavedNote(draft))}
              disabled={isSaving}
              aria-busy={isSaving}
              data-testid="note-unsaved-save"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--danger btn--sm"
              onClick={onClose}
              disabled={isSaving}
              data-testid="note-unsaved-discard"
            >
              Discard
            </button>
          </div>
        )}
      </div>
      {!confirmingDiscard && (
        <>
          <div className="note-pop__footer">
            <span className="note-pop__footer-text">Visible to your approver</span>
            <div className="note-pop__actions">
              {initialNote !== null && initialNote !== '' && (
                <button
                  type="button"
                  className="btn btn--ghost btn--danger btn--sm"
                  onClick={() => onSave(null)}
                  disabled={isSaving}
                  data-testid="note-remove"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn primary btn--sm"
                onClick={() => onSave(draft.trim() || null)}
                disabled={isSaving}
                aria-busy={isSaving}
                data-testid="note-save"
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          <div className="note-pop__hint">⌘↵ / Ctrl↵ to save · Esc to cancel</div>
        </>
      )}
    </div>
  );
}
